import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { publicApi } from "@/lib/api";
import { ListingCard, PageShell, Empty } from "@/components/common";
import { CitySelect } from "@/components/DeliveryQuoteForm";
import { useMe } from "@/context/MeContext";

function Sel({ value, onChange, options, testid, all = "Tous" }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="bg-white"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{all}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Catalogue() {
  const [params, setParams] = useSearchParams();
  const { meta } = useMe();
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const get = (k) => params.get(k) || "";
  const set = (k) => (v) => {
    const p = new URLSearchParams(params);
    const val = v?.target ? v.target.value : v;
    val ? p.set(k, val) : p.delete(k);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      publicApi.get(`/listings?${params.toString()}&limit=60`).then((r) => { setData(r.data); setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [params]);

  return (
    <PageShell eyebrow={`${data.total} annonce${data.total > 1 ? "s" : ""}`} title="Catalogue">
      <div className="grid lg:grid-cols-12 gap-8">
        <button data-testid="filters-toggle-btn" onClick={() => setShowFilters(!showFilters)} className="lg:hidden flex items-center justify-center gap-2 rounded-full border border-stone-200 bg-white py-2.5 text-sm font-semibold">
          <SlidersHorizontal className="w-4 h-4" /> {showFilters ? "Masquer les filtres" : "Afficher les filtres"}
        </button>
        <aside className={`${showFilters ? "block" : "hidden"} lg:block lg:col-span-3 space-y-5 bg-white rounded-2xl border border-stone-200 p-6 h-fit lg:sticky lg:top-24`} data-testid="catalogue-filters">
          <p className="flex items-center gap-2 font-semibold text-forest"><SlidersHorizontal className="w-4 h-4" /> Filtres</p>
          <div><Label>Recherche</Label><Input data-testid="filter-q" value={get("q")} onChange={set("q")} placeholder="Mot-clé" className="bg-white" /></div>
          <div><Label>Type</Label><Sel testid="filter-type" value={get("type")} onChange={set("type")} options={[["sale", "Vente"], ["donation", "Don"]]} /></div>
          <div><Label>Catégorie</Label><Sel testid="filter-category" value={get("category")} onChange={set("category")} all="Toutes" options={meta.categories.map((c) => [c, c])} /></div>
          <div><Label>État</Label><Sel testid="filter-condition" value={get("condition")} onChange={set("condition")} options={meta.conditions.map((c) => [c, c])} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Prix min</Label><Input data-testid="filter-min-price" type="number" value={get("min_price")} onChange={set("min_price")} className="bg-white" /></div>
            <div><Label>Prix max</Label><Input data-testid="filter-max-price" type="number" value={get("max_price")} onChange={set("max_price")} className="bg-white" /></div>
          </div>
          <div><Label>Près de</Label><CitySelect testid="filter-city" value={get("city")} onChange={set("city")} placeholder="Choisir une ville" /></div>
          <div><Label>Rayon</Label><Sel testid="filter-radius" value={get("radius_km")} onChange={set("radius_km")} all="Toute la zone" options={[["2", "2 km"], ["5", "5 km"], ["10", "10 km"], ["20", "20 km"]]} /></div>
          <button data-testid="filter-reset" onClick={() => setParams({})} className="text-xs text-stone-500 underline">Réinitialiser</button>
        </aside>
        <div className="lg:col-span-9">
          <div className="flex justify-end mb-4">
            <div className="w-56"><Select value={get("sort") || "recent"} onValueChange={set("sort")}>
              <SelectTrigger data-testid="sort-select" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Plus récentes</SelectItem>
                <SelectItem value="price_asc">Prix croissant</SelectItem>
                <SelectItem value="price_desc">Prix décroissant</SelectItem>
                <SelectItem value="distance">Distance</SelectItem>
              </SelectContent>
            </Select></div>
          </div>
          {!loading && !data.items.length ? <Empty testid="catalogue-empty">Aucune annonce ne correspond à vos critères.</Empty> : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6" data-testid="catalogue-grid">
              {data.items.map((l, i) => <ListingCard key={l.id} l={l} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
